/**
 * AICC Identity Utility
 * 
 * Shared utility for appending user identification parameters to AICC/SCORM iframe URLs.
 * This enables seamless login/identification in external learning content.
 * 
 * Used by:
 * - CoursePlayer.jsx (AICCPlayer component)
 * - ExternalCoursePlayer.jsx
 * - ExternalLearningPlayer.jsx
 */

/**
 * Appends user identification parameters to an AICC/iframe URL
 * 
 * @param {string} url - The base iframe URL
 * @param {Object} user - The user object from auth context
 * @param {string} user.user_id - User's unique identifier
 * @param {string} user.full_name - User's full name (or fullName or name)
 * @param {string} user.email - User's email address
 * @returns {string} - The URL with appended user params, or original URL if invalid
 */
export function appendAiccUserParams(url, user) {
  if (!url || !user) return url;
  
  try {
    const urlObj = new URL(url);
    
    // Add external_id (user's stable internal ID - user_id from the database)
    if (user.user_id) {
      urlObj.searchParams.append('external_id', user.user_id);
    }
    
    // Add name (full display name) - check multiple possible field names
    const userName = user.full_name || user.fullName || user.name;
    if (userName) {
      urlObj.searchParams.append('name', userName);
    }
    
    // Add email
    if (user.email) {
      urlObj.searchParams.append('email', user.email);
    }
    
    return urlObj.toString();
  } catch (e) {
    // If URL parsing fails (malformed URL), try manual append
    console.warn('AICC URL parsing failed, using manual append:', e);
    const separator = url.includes('?') ? '&' : '?';
    let params = [];
    
    if (user.user_id) {
      params.push(`external_id=${encodeURIComponent(user.user_id)}`);
    }
    
    const userName = user.full_name || user.fullName || user.name;
    if (userName) {
      params.push(`name=${encodeURIComponent(userName)}`);
    }
    
    if (user.email) {
      params.push(`email=${encodeURIComponent(user.email)}`);
    }
    
    return params.length > 0 ? `${url}${separator}${params.join('&')}` : url;
  }
}

/**
 * Creates a memoized version of appendAiccUserParams for use in React components
 * Call this with the user object to get a function that only takes the URL
 * 
 * @param {Object} user - The user object from auth context
 * @returns {Function} - A function that takes a URL and returns the URL with user params
 */
export function createAiccUrlAppender(user) {
  return (url) => appendAiccUserParams(url, user);
}

export default appendAiccUserParams;
